import { useEffect, useMemo, useState } from "react";

import {
  addNewAdherent,
  deleteAdherent,
  getAdherentsByUser,
  updateAdherent,
} from "../../../../services/adherentServices";

import { sendNotificationToAdmin } from "../../../../services/notificationServices";

import {
  EMPTY_JUDOKA,
  getCurrentUserId,
  normalizeAdherentsResponse,
  normalizeJudokaForForm,
  prepareJudokaPayload,
  isValidEmailAddress,
  isValidHttpUrl,
  isValidPhoneNumber,
} from "../utils/judokaProfileUtils";

export function usePageJudoka({ addNotification }) {
  const [adherents, setAdherents] = useState([]);
  const [selectedJudokaId, setSelectedJudokaId] = useState(null);
  const [localJudoka, setLocalJudoka] = useState({
    ...EMPTY_JUDOKA,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUrlError, setPhotoUrlError] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [weightSaving, setWeightSaving] = useState(false);
  const [quickWeight, setQuickWeight] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const judoka = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find(
        (item) => Number(item.id) === Number(selectedJudokaId),
      ) || adherents[0]
    );
  }, [adherents, selectedJudokaId]);

  useEffect(() => {
    setQuickWeight(judoka?.poids ?? "");
  }, [judoka?.poids, judoka?.id]);

  useEffect(() => {
    let mounted = true;

    const fetchJudokaData = async () => {
      setLoading(true);

      try {
        const response = await getAdherentsByUser();
        const loadedAdherents = normalizeAdherentsResponse(response);

        if (!mounted) return;

        setAdherents(loadedAdherents);

        if (loadedAdherents.length > 0) {
          const keepSelected = loadedAdherents.some(
            (item) =>
              Number(item.id) === Number(selectedJudokaId),
          );

          const nextSelectedId = keepSelected
            ? selectedJudokaId
            : loadedAdherents[0].id;

          const nextJudoka =
            loadedAdherents.find(
              (item) =>
                Number(item.id) === Number(nextSelectedId),
            ) || loadedAdherents[0];

          setSelectedJudokaId(nextSelectedId);
          setLocalJudoka(normalizeJudokaForForm(nextJudoka));
          setIsEditing(false);
        } else {
          setSelectedJudokaId(null);
          setLocalJudoka({
            ...EMPTY_JUDOKA,
          });
          setIsEditing(true);

          addNotification(
            "Aucun profil trouvé. Veuillez en créer un.",
            "info",
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données des judokas :",
          error,
        );

        if (mounted) {
          addNotification(
            "Erreur lors de la récupération des données des judokas",
            "error",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchJudokaData();

    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const getTokenOrNotify = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      addNotification(
        "Vous devez être connecté pour effectuer cette action.",
        "error",
      );

      return null;
    }

    return token;
  };

  const replaceAdherentInState = (updatedAdherent) => {
    setAdherents((prevAdherents) =>
      prevAdherents.map((item) =>
        Number(item.id) === Number(updatedAdherent.id)
          ? {
              ...item,
              ...updatedAdherent,
            }
          : item,
      ),
    );
  };

  const selectJudoka = (id) => {
    const selected = adherents.find(
      (item) => Number(item.id) === Number(id),
    );

    setSelectedJudokaId(id);

    if (selected) {
      setLocalJudoka(normalizeJudokaForForm(selected));
    }
  };

  const openCreateModal = () => {
    setLocalJudoka({
      ...EMPTY_JUDOKA,
    });

    setIsEditing(true);
  };

  const openEditModal = () => {
    if (judoka) {
      setLocalJudoka(normalizeJudokaForForm(judoka));
    }

    setIsEditing(true);
  };

  const openPhotoModal = () => {
    setPhotoUrl("");
    setPhotoUrlError("");
    setSelectedAvatar("");
    setIsPhotoModalOpen(true);
  };

  const closeModals = () => {
    setIsEditing(false);
    setIsPhotoModalOpen(false);
    setSaving(false);
    setPhotoUrl("");
    setPhotoUrlError("");
    setSelectedAvatar("");
    setIsDeleteModalOpen(false);
    setDeleting(false);

    if (judoka) {
      setLocalJudoka(normalizeJudokaForForm(judoka));
    } else {
      setLocalJudoka({
        ...EMPTY_JUDOKA,
      });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setLocalJudoka((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCountryChange = (event) => {
    const { name, value } = event.target;

    setLocalJudoka((prevState) => ({
      ...prevState,
      [name]: value,
      departement: "",
    }));
  };

  const validateJudokaForm = () => {
    if (
      localJudoka.licence_url &&
      !isValidHttpUrl(localJudoka.licence_url)
    ) {
      addNotification(
        "Le lien de licence doit commencer par http:// ou https://.",
        "error",
      );

      return false;
    }

    if (!isValidPhoneNumber(localJudoka.tel_adherent)) {
      addNotification(
        "Le numéro de téléphone renseigné n’est pas valide.",
        "error",
      );

      return false;
    }

    if (!isValidEmailAddress(localJudoka.mail_adherent)) {
      addNotification(
        "L’adresse e-mail renseignée n’est pas valide.",
        "error",
      );

      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const token = getTokenOrNotify();

    if (!token) return;
    if (!validateJudokaForm()) return;

    const userId = getCurrentUserId();
    const preparedPayload = prepareJudokaPayload(localJudoka);

    setSaving(true);

    try {
      if (judoka && localJudoka.id) {
        await updateAdherent(
          localJudoka.id,
          preparedPayload,
          token,
        );

        const updatedJudoka = {
          ...judoka,
          ...preparedPayload,
          id: localJudoka.id,
        };

        replaceAdherentInState(updatedJudoka);
        setLocalJudoka(normalizeJudokaForForm(updatedJudoka));

        addNotification(
          "Profil Judoka mis à jour avec succès !",
          "success",
        );

        try {
          const message = `Le profil de ${localJudoka.prenom} ${localJudoka.nom} a été mis à jour.`;

          await sendNotificationToAdmin(message, token);
        } catch (notificationError) {
          console.error(
            "Erreur lors de l’envoi de la notification administrateur :",
            notificationError,
          );
        }
      } else {
        const payload = {
          ...preparedPayload,
          user_id: userId,
          userId,
        };

        const newJudoka = await addNewAdherent(payload, token);

        setAdherents((prevAdherents) => [
          ...prevAdherents,
          newJudoka,
        ]);

        setSelectedJudokaId(newJudoka.id);
        setLocalJudoka(normalizeJudokaForForm(newJudoka));

        addNotification(
          "Profil Judoka créé avec succès !",
          "success",
        );
      }

      setIsEditing(false);
      setRefreshKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error(
        "Erreur lors de la soumission des données du judoka :",
        error,
      );

      const message =
        error?.response?.data?.message ||
        "Erreur lors de la soumission des données du judoka";

      addNotification(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const updatePhoto = async (photoValue) => {
    if (!judoka?.id) {
      addNotification(
        "Veuillez d’abord créer le profil judoka.",
        "error",
      );

      return;
    }

    const token = getTokenOrNotify();

    if (!token) return;

    setSaving(true);

    try {
      const payload = prepareJudokaPayload({
        ...normalizeJudokaForForm(judoka),
        photo_url: photoValue,
      });

      await updateAdherent(judoka.id, payload, token);

      const updatedJudoka = {
        ...judoka,
        ...payload,
        photo_url: photoValue,
      };

      replaceAdherentInState(updatedJudoka);
      setLocalJudoka(normalizeJudokaForForm(updatedJudoka));

      addNotification(
        "Photo mise à jour avec succès !",
        "success",
      );

      closeModals();
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de la photo :",
        error,
      );

      addNotification(
        "Erreur lors de la mise à jour de la photo.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPreview = (avatarUrl) => {
    setSelectedAvatar(avatarUrl);
    setPhotoUrl("");
    setPhotoUrlError("");
  };

  const handleAvatarSubmit = () => {
    if (!selectedAvatar) {
      addNotification(
        "Veuillez sélectionner une photo.",
        "error",
      );

      return;
    }

    updatePhoto(selectedAvatar);
  };

  const handlePhotoUrlChange = (value) => {
    setPhotoUrl(value);
    setPhotoUrlError("");

    if (selectedAvatar) {
      setSelectedAvatar("");
    }
  };

  const handlePhotoUrlSubmit = (event) => {
    event.preventDefault();

    const cleanUrl = String(photoUrl || "").trim();

    if (!cleanUrl) {
      setPhotoUrlError(
        "Veuillez renseigner une URL d’image.",
      );

      return;
    }

    if (!isValidHttpUrl(cleanUrl)) {
      setPhotoUrlError(
        "L’URL doit commencer par http:// ou https://.",
      );

      return;
    }

    updatePhoto(cleanUrl);
  };

  const handleQuickWeightChange = (value) => {
    if (value === "") {
      setQuickWeight("");

      return;
    }

    const numericValue = Number(value);

    if (
      !Number.isFinite(numericValue) ||
      numericValue < 0
    ) {
      return;
    }

    setQuickWeight(value);
  };

  const handleQuickWeightAdjust = (delta) => {
    const currentValue = Number(
      quickWeight || judoka?.poids || 0,
    );

    const nextValue =
      Math.max(
        0,
        Math.round((currentValue + delta) * 10) / 10,
      );

    setQuickWeight(String(nextValue));
  };

  const handleQuickWeightSave = async () => {
    if (!judoka?.id) return;

    const token = getTokenOrNotify();

    if (!token) return;

    const numericWeight = Number(quickWeight);

    if (
      !Number.isFinite(numericWeight) ||
      numericWeight <= 0
    ) {
      addNotification(
        "Veuillez renseigner un poids valide.",
        "error",
      );

      return;
    }

    setWeightSaving(true);

    try {
      const payload = prepareJudokaPayload({
        ...normalizeJudokaForForm(judoka),
        poids: numericWeight,
      });

      await updateAdherent(judoka.id, payload, token);

      const updatedJudoka = {
        ...judoka,
        ...payload,
        poids: numericWeight,
      };

      replaceAdherentInState(updatedJudoka);
      setLocalJudoka(normalizeJudokaForForm(updatedJudoka));
      setQuickWeight(String(numericWeight));

      addNotification(
        "Poids mis à jour avec succès !",
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour du poids :",
        error,
      );

      addNotification(
        "Erreur lors de la mise à jour du poids.",
        "error",
      );
    } finally {
      setWeightSaving(false);
    }
  };

  const openDeleteModal = () => {
    if (!judoka?.id) {
      addNotification(
        "Aucun profil judoka sélectionné.",
        "error",
      );

      return;
    }

    setIsDeleteModalOpen(true);
  };

  const handleDeleteJudoka = async () => {
    if (!judoka?.id) {
      addNotification(
        "Aucun profil judoka sélectionné.",
        "error",
      );

      return;
    }

    const token = getTokenOrNotify();

    if (!token) return;

    const deletedJudokaId = judoka.id;

    const deletedJudokaName =
      [judoka.prenom, judoka.nom]
        .filter(Boolean)
        .join(" ") || "Le judoka";

    setDeleting(true);

    try {
      await deleteAdherent(deletedJudokaId, token);

      const nextAdherents = adherents.filter(
        (item) =>
          Number(item.id) !== Number(deletedJudokaId),
      );

      const nextSelectedJudoka =
        nextAdherents[0] || null;

      setAdherents(nextAdherents);
      setSelectedJudokaId(
        nextSelectedJudoka?.id || null,
      );

      setLocalJudoka(
        nextSelectedJudoka
          ? normalizeJudokaForForm(nextSelectedJudoka)
          : {
              ...EMPTY_JUDOKA,
            },
      );

      setIsDeleteModalOpen(false);

      if (!nextAdherents.length) {
        setIsEditing(true);
      }

      addNotification(
        `${deletedJudokaName} a été supprimé.`,
        "success",
      );

      try {
        await sendNotificationToAdmin(
          `Le profil judoka de ${deletedJudokaName} a été supprimé.`,
          token,
        );
      } catch (notificationError) {
        console.error(
          "Erreur lors de l'envoi de la notification admin après suppression :",
          notificationError,
        );
      }
    } catch (error) {
      console.error(
        "Erreur lors de la suppression du judoka :",
        error,
      );

      const message =
        error?.response?.data?.message ||
        "Erreur lors de la suppression du profil judoka.";

      addNotification(message, "error");
    } finally {
      setDeleting(false);
    }
  };

  return {
    adherents,
    judoka,
    selectedJudokaId,
    localJudoka,

    isEditing,
    isPhotoModalOpen,

    selectedAvatar,
    photoUrl,
    photoUrlError,

    loading,
    saving,
    weightSaving,
    quickWeight,

    selectJudoka,
    openCreateModal,
    openEditModal,
    openPhotoModal,
    closeModals,

    handleChange,
    handleCountryChange,
    handleSubmit,

    handleAvatarPreview,
    handleAvatarSubmit,
    handlePhotoUrlChange,
    handlePhotoUrlSubmit,

    handleQuickWeightChange,
    handleQuickWeightAdjust,
    handleQuickWeightSave,

    isDeleteModalOpen,
    deleting,
    openDeleteModal,
    handleDeleteJudoka,
  };
}